/**
 * H19: the horse and the bamboo log in every story. Origin A: the two quests inside the plains chain (OA_QUESTS, after
 * the bandits) are accepted, counted and handed in here, in the shared quest log. Origins B-D: their own starter
 * chain (data/story/originChains.ts) on the greybox towns, tracked by `og-done-<quest>` scene marks (the shared log is
 * left alone). The horse keeper hands out ONE starter horse and the ferryman ONE bamboo log, each only while that
 * quest is under way. Objectives: talk, ride a spot mounted, pole a spot on water, kill in the pocket.
 * TODO-polish: text is skeleton copy. Idle outside the zones named in the data.
 */
import { originOf } from '../data/origins';
import { advanceObjectives } from '../questProgress';
import {
  FERRYMAN,
  HORSEKEEPER_A,
  HORSE_SPOT_A,
  LOG_SPOT,
  ORIGIN_CHAINS,
  OA,
  STARTER_HORSE,
  STARTER_LOG,
  type RideSpot,
  type StoryNpcDef,
} from '../data/story/originChains';
import type { QuestDef } from '../data/quests';
import { FULL_CHAINS } from '../data/story/originFull';
import { OriginFullRunner } from './originFull';
import { isWaterAt } from '../qinggong';
import { plains256 } from '../data/zones';
import type { Entity, PlayerCommand, World, WorldEvent } from '../world';
import type { SystemHost, WorldSystem } from './types';

type Hit = 'talk' | 'visit' | 'kill';

interface Track {
  q: QuestDef;
  /** Chain B-D: marks the quest done, pays, and moves on. Chain A: null (the quest log does it). */
  prog: number[];
}

const doneMark = (id: string): string => `og-done-${id}`;

export class OriginStorySystem implements WorldSystem {
  readonly name = 'originStory';
  private npcs = new Map<string, Entity>();
  private prog = new Map<string, number[]>();
  /** The origin's full chain (data/story/originFull.ts) when it has one: it runs instead of the stub below. */
  private full: OriginFullRunner | null = null;

  constructor(private readonly h: SystemHost) {}

  private get w(): World {
    return this.h.world;
  }

  setup(): void {
    const w = this.w;
    if (w.map.interior) return;
    const zone = w.map.zone as string;
    const mk = (d: StoryNpcDef) => {
      const e = this.h.add({ kind: 'npc', x: d.at[0], y: d.at[1], name: d.name, title: d.title, sprite: d.sprite, facing: 1 });
      this.npcs.set(`${zone}:${d.role}`, e);
    };
    if (zone === 'plains' && plains256()) {
      mk(HORSEKEEPER_A);
      mk(FERRYMAN);
    }
    const fullDef = FULL_CHAINS[originOf(w.progress) as 'b' | 'c' | 'd'];
    if (fullDef) {
      this.full = new OriginFullRunner(this.h, fullDef);
      this.full.setup();
    }
    for (const c of Object.values(ORIGIN_CHAINS)) {
      if (zone === c.zone && !FULL_CHAINS[c.origin]) {
        mk(c.keeper);
        mk(c.horsekeeper);
      }
    }
  }

  /** The quest the player is on in this system's chains, if any, with its progress. */
  private track(): Track | null {
    const w = this.w;
    if (w.map.interior) return null;
    const zone = w.map.zone as string;
    const o = originOf(w.progress);
    if (o === 'a') {
      if (zone !== 'plains' || !plains256()) return null;
      const q = w.currentQuest();
      if (!q || (q.id !== OA.horse && q.id !== OA.log) || w.progress.quest.status !== 'active') return null;
      return { q, prog: w.progress.quest.progress };
    }
    if (FULL_CHAINS[o]) return null;
    const c = ORIGIN_CHAINS[o];
    const q = c.quests.find((x) => !w.progress.scenes.includes(doneMark(x.id)));
    if (!q) return null;
    const inTown = zone === c.zone;
    const isLog = q.id === c.quests[c.quests.length - 1]!.id;
    if (isLog ? zone !== 'plains' : !inTown) return null;
    let p = this.prog.get(q.id);
    if (!p) this.prog.set(q.id, (p = []));
    return { q, prog: p };
  }

  /** Counts one hit on the current quest's matching objectives. */
  private hit(type: Hit, target: string): void {
    const t = this.track();
    if (!t) return;
    const r = advanceObjectives(t.q, t.prog, type, target);
    if (!r) return;
    const w = this.w;
    if (r === 'progress') {
      this.h.emit({ type: 'quest', status: 'progress', questId: t.q.id });
      return;
    }
    if (originOf(w.progress) === 'a') {
      w.progress.quest.status = 'ready';
      this.h.emit({ type: 'quest', status: 'ready', questId: t.q.id });
    } else this.finish(t.q);
  }

  private finish(q: QuestDef): void {
    const w = this.w;
    w.progress.scenes.push(doneMark(q.id));
    this.h.grantReward({ gold: q.rewards.gold, cultivation: q.rewards.cultivation });
    this.h.emit({ type: 'quest', status: 'completed', questId: q.id });
    this.h.notice(`${q.title}: ${q.done}`);
  }

  private grant(mount: string): void {
    const pr = this.w.progress;
    if (pr.mounts.includes(mount)) return;
    pr.mounts.push(mount);
    pr.mountPick = mount;
    this.h.emit({ type: 'progressChanged' });
  }

  talk(npc: Entity): boolean {
    let role: string | null = null;
    for (const [k, e] of this.npcs) if (e === npc) role = k.split(':')[1]!;
    if (this.full?.talk(npc, role ?? undefined)) return true;
    if (!role) return false;
    const t = this.track();
    const wanted = t?.q.objectives.some((o) => o.type === 'talk' && o.target === role);
    let text: string;
    if (role === 'horsekeeper') {
      if (wanted) this.grant(STARTER_HORSE);
      text = wanted
        ? 'ม้าตัวนี้ยกให้เจ้า กดปุ่มขี่พาหนะเพื่อขึ้นม้า กดอีกครั้งเพื่อลง ขี่เร็วกว่าเดิน ฝากและเรียกม้าที่สถานีม้า' // TODO-polish
        : 'สถานีม้ารับฝากและเรียกม้า ขี่ม้าไปได้ทุกถนน'; // TODO-polish
    } else if (role === 'ferryman') {
      if (wanted) this.grant(STARTER_LOG);
      text = wanted
        ? 'ไม้ไผ่ท่อนนี้ยกให้เจ้า เลือกไผ่ลำเดียวเป็นพาหนะแล้วกดขี่บนน้ำ ยืนบนลำแล้วพายด้วยไม้ไผ่ยาวไปช้าๆ' // TODO-polish
        : 'น้ำลึกอย่าขี่ม้าลง ใช้ไม้ไผ่พายข้าม'; // TODO-polish
    } else text = t?.q.objectives.some((o) => o.target === 'keeper') ? t.q.done : 'ยินดีต้อนรับ'; // TODO-polish
    this.h.emit({ type: 'dialog', npcId: npc.id, text, action: null });
    if (wanted) this.hit('talk', role);
    return true;
  }

  refuse(cmd: PlayerCommand): boolean {
    return this.full?.refuse(cmd) ?? false;
  }

  observe(ev: WorldEvent): void {
    this.full?.observe(ev);
    if (ev.type !== 'death') return;
    const e = this.w.entities.get(ev.id);
    if (e && e.kind === 'monster') this.hit('kill', 'wolf');
  }

  private inSpot(s: RideSpot): boolean {
    const w = this.w;
    const p = w.player;
    if (Math.hypot(p.x - s.at[0], p.y - s.at[1]) > s.r) return false;
    if (s.mount && p.mount !== s.mount) return false;
    return !s.water || isWaterAt(w.map, p.x, p.y);
  }

  /** Test and GM hook: finishes the current quest outright (the fight, when the pocket is not played). */
  force(): void {
    const t = this.track();
    if (!t) return;
    t.q.objectives.forEach((o, i) => (t.prog[i] = o.count));
    if (originOf(this.w.progress) === 'a') {
      this.w.progress.quest.status = 'ready';
    } else this.finish(t.q);
  }

  step(dt: number): void {
    const w = this.w;
    if (w.map.interior) return;
    this.full?.step(dt);
    const o = originOf(w.progress);
    if (o === 'a' && plains256() && w.map.zone === 'plains') {
      const q = w.currentQuest();
      if (q && (q.id === OA.horse || q.id === OA.log)) {
        if (w.progress.quest.status === 'available') w.apply({ t: 'questAccept' });
        if (w.progress.quest.status === 'ready') w.apply({ t: 'questTurnIn' });
      }
    }
    const t = this.track();
    if (!t) return;
    const id = t.q.id;
    this.hit('visit', `og-${o}-arrive`);
    if (id === OA.horse) {
      if (this.inSpot(HORSE_SPOT_A)) this.hit('visit', 'horse-ride');
    } else if (id === OA.log || id.endsWith('-bamboo')) {
      if (this.inSpot(LOG_SPOT)) this.hit('visit', 'log-ride');
    } else if (o !== 'a') {
      const c = ORIGIN_CHAINS[o];
      if (id.endsWith('-horse') && this.inSpot(c.horseSpot)) this.hit('visit', `og-${o}-horse-ride`);
      if (id.endsWith('-exit') && this.inSpot(c.exitSpot)) this.hit('visit', `og-${o}-exit-ride`);
    }
  }
}
